import { Trans } from '@lingui/react/macro';
import { ChevronLeft } from '@untitledui/icons/ChevronLeft';
import { ChevronRight } from '@untitledui/icons/ChevronRight';
import { Star01 } from '@untitledui/icons/Star01';
import { useState, useEffect, useCallback } from 'react';

import { BASE_PATH } from '@/constants/config';
import { cx } from '@/utils/cx';

const AUTO_ADVANCE_MS = 7000;

const slides = [Slide1, Slide2, Slide3];

export function SignupCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % 3);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + 3) % 3);
  }, []);

  useEffect(() => {
    const timer = setInterval(goNext, AUTO_ADVANCE_MS);
    return () => {
      clearInterval(timer);
    };
  }, [goNext]);

  return (
    <div className="relative flex size-full flex-col items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, #080055 0%, #0002a1 40%, #080055 100%)',
        }}
      />
      {/* Purple glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 70% 15%, rgba(120, 0, 220, 0.5) 0%, transparent 55%)',
        }}
      />
      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* Slide content */}
      <div className="relative z-10 flex w-full max-w-[600px] flex-1 flex-col items-center justify-center px-8 pt-10">
        {slides.map((SlideComponent, index) => {
          const isActive = index === activeIndex;
          return (
            <div
              // oxlint-disable-next-line react/no-array-index-key -- static slides array, index is stable
              key={index}
              aria-hidden={!isActive}
              // oxlint-disable-next-line no-negated-condition
              inert={!isActive ? true : undefined}
              className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out"
              style={{
                opacity: isActive ? 1 : 0,
                transform: `translateX(${isActive ? 0 : index < activeIndex ? -40 : 40}px)`,
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <SlideComponent />
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="relative z-10 flex w-full items-center justify-center gap-16 pb-6">
        <button
          type="button"
          onClick={goPrev}
          className="rounded-lg p-2 text-white/60 transition-colors hover:text-white"
          aria-label="Previous slide"
        >
          <ChevronLeft className="size-5" />
        </button>

        <div className="flex items-center gap-4">
          {[0, 1, 2].map((index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                setActiveIndex(index);
              }}
              className={cx(
                'size-2.5 rounded-full transition-all duration-300',
                index === activeIndex ? 'bg-white' : 'bg-white/30',
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          className="rounded-lg p-2 text-white/60 transition-colors hover:text-white"
          aria-label="Next slide"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>
    </div>
  );
}

/* ─── Slide 1: Project Cards ─── */
function Slide1() {
  return (
    <div className="flex flex-col items-center gap-20">
      {/* Cards visual */}
      <div className="relative">
        {/* Main card with project listings */}
        <div className="rounded-[32px] border border-white/30 bg-white/10 p-6 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/project-list-card.png`}
            alt="Example research projects"
            className="w-[416px] rounded-2xl"
          />
        </div>

        {/* Floating card */}
        <div className="absolute -right-11 -bottom-9 shadow-2xl">
          <img
            src={`${BASE_PATH}/project-card.png`}
            alt="Video call project - $125 USD"
            className="w-[360px] rounded-2xl"
          />
        </div>
      </div>

      {/* Text */}
      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="font-heading text-display-xs font-semibold text-white">
          <Trans>Get paid to share your opinion</Trans>
        </h2>
        <p className="max-w-[456px] text-md font-medium text-white/75">
          <Trans>
            Find paid remote and in-person research studies you&apos;ll be
            well-compensated for.
          </Trans>
        </p>
      </div>
    </div>
  );
}

/* ─── Slide 2: Payment Rewards ─── */
function Slide2() {
  return (
    <div className="flex flex-col items-center gap-20">
      {/* Brand logos layout */}
      <div className="relative h-[304px] w-[590px]">
        {/* Amazon */}
        <div className="absolute top-[15px] left-[51px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/amazon-card.png`}
            alt="Amazon"
            className="h-[50px] w-20 rounded-md object-contain"
          />
        </div>

        {/* eBay */}
        <div className="absolute top-[-7px] left-[216px] rounded-lg border border-white/30 bg-white/10 p-1.5 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/ebay-card.png`}
            alt="eBay"
            className="h-[30px] w-12 rounded-sm object-contain"
          />
        </div>

        {/* Apple */}
        <div className="absolute top-[-2px] left-[412px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/apple-card.png`}
            alt="Apple"
            className="h-10 w-16 rounded-md object-contain"
          />
        </div>

        {/* Visa */}
        <div className="absolute top-[116px] left-[3px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/visa-card.png`}
            alt="Visa"
            className="h-[72px] w-[116px] rounded-md object-contain"
          />
        </div>

        {/* Airbnb */}
        <div className="absolute top-[110px] left-[461px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/airbnb-card.png`}
            alt="Airbnb"
            className="h-[65px] w-[115px] rounded-md object-contain"
          />
        </div>

        {/* DoorDash */}
        <div className="absolute top-[241px] left-[94px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/doordash-card.png`}
            alt="DoorDash"
            className="h-[56px] w-[90px] rounded-md object-contain"
          />
        </div>

        {/* Google Play */}
        <div className="absolute top-[249px] left-[331px] rounded-lg border border-white/30 bg-white/10 p-1.5 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/google-play-card.png`}
            alt="Google Play"
            className="h-[34px] w-[54px] rounded-sm object-contain"
          />
        </div>

        {/* Uber Eats */}
        <div className="absolute top-[212px] left-[447px] rounded-xl border border-white/30 bg-white/10 p-2 backdrop-blur-sm">
          <img
            src={`${BASE_PATH}/ubereats-card.png`}
            alt="Uber Eats"
            className="h-[55px] w-[88px] rounded-md object-contain"
          />
        </div>

        {/* Center earnings card */}
        <div className="absolute top-1/2 left-[52%] -translate-x-1/2 -translate-y-1/2 rounded-xl p-4">
          <img
            src={`${BASE_PATH}/chris-profile-card.png`}
            alt="Earnings profile showing $330.50 USD total earned"
            className="w-[281px] rounded-2xl shadow-2xl"
          />
        </div>
      </div>

      {/* Text */}
      <div className="flex flex-col items-center gap-2 text-center">
        <h2 className="font-heading text-display-xs font-semibold text-white">
          <Trans>Turn Your Insights Into Rewards</Trans>
        </h2>
        <p className="max-w-[600px] text-md font-medium text-white/75">
          <Trans>
            Choose from a variety of popular gift cards and payment methods when
            you complete projects. Track your total earnings as you go.
          </Trans>
        </p>
      </div>
    </div>
  );
}

/* ─── Slide 3: Testimonial ─── */
function Slide3() {
  return (
    <div className="flex max-w-[560px] flex-col items-center justify-center gap-8">
      {/* Stars */}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star01
            key={`star-${String(n)}`}
            className="size-5 fill-warning-400 text-warning-400"
          />
        ))}
      </div>

      {/* Quote */}
      <p className="text-center font-heading text-display-sm font-medium text-white">
        <Trans>
          It&apos;s a cool way of spending quality time and earn some money as
          well as helping to shape decisions.
        </Trans>
      </p>

      {/* Attribution */}
      <div className="flex flex-col items-center gap-4">
        <img
          src={`${BASE_PATH}/lori-avatar.png`}
          alt="Lori Bryson"
          className="size-16 rounded-full border border-black/8 object-cover"
        />
        <div className="text-center">
          <p className="text-md font-semibold text-white">Lori Bryson</p>
          <p className="text-sm font-medium text-white/50">Gig worker</p>
        </div>
      </div>
    </div>
  );
}
